import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { ProgressBar } from "primereact/progressbar";
import { Panel } from "primereact/panel";
import { emojify } from "node-emoji";
import toast from "react-hot-toast";
import { DateBodyTemplate, UserPhoneBodyTemplate } from "../../components";
import { User } from "../../models/users";
import { UserSegmentationFilters } from "../../components/Filters/UserSegmentationFilters";
import { useLazyGetSegmentedUsersQuery, useSendCustomMessageMutation } from "../../store/services/custom-messages";
import { UserSegmentationFilters as FilterTypes, BulkMessageResponseData } from "../../models/custom-messages";
import { useGetEventsQuery } from "../../store/services/events";
import { Dropdown } from "primereact/dropdown";
import { useDebounce } from "../../hooks/useDebounce";
import { isUserRestrictedByBan } from "../../utils/functions/users";
import { useNavigate } from "react-router-dom";


const MemoizedUserCheckbox = React.memo(({
  id,
  user,
  isSelected,
  onToggle
}: {
  id: number;
  user: User;
  isSelected: boolean;
  onToggle: (user: User) => void;
}) => {
  return (
    <div className="flex justify-content-center">
      <Checkbox
        inputId={`select-user-${id}`}
        onChange={() => onToggle(user)}
        checked={isSelected}
        className={isSelected ? 'p-highlight' : ''}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.isSelected === nextProps.isSelected;
});

export const SendCustomMessages: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = React.useState<string>("");
  const [content, setContent] = React.useState<string>("");
  const [url, setUrl] = React.useState<string>("");
  const [eventId, setEventId] = React.useState<number>(0);
  const [email, setEmail] = React.useState<string>("");
  const [firstName, setFirstName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [birthDateMonth, setBirthDateMonth] = React.useState<number | undefined>();
  const [birthDateDay, setBirthDateDay] = React.useState<number | undefined>();
  const [eventIds, setEventIds] = React.useState<number[]>([]);
  const [eventTypeId, setEventTypeId] = React.useState<number | undefined>();
  const [eventSubTypeId, setEventSubTypeId] = React.useState<number | undefined>();
  const [behaviorType, setBehaviorType] = React.useState<'registered' | 'saved' | 'both' | undefined>();
  const [tenantId, setTenantId] = React.useState<string | undefined>();


  const [selectedUsersMap, setSelectedUsersMap] = React.useState<Map<number, User>>(new Map());
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [isSending, setIsSending] = React.useState<boolean>(false);
  const [sendOutcome, setSendOutcome] = React.useState<{ errorMessage?: string; stats?: BulkMessageResponseData['stats']; unmatchedEmails?: string[] } | null>(null);

  const [getSegmentedUsers, { data: segmentedData, isLoading }] = useLazyGetSegmentedUsersQuery();
  const [sendCustomMessage] = useSendCustomMessageMutation();
  const [totalToSend, setTotalToSend] = React.useState<number>(0);
  const [processedCount, setProcessedCount] = React.useState<number>(0);
  
  const [eventSearch, setEventSearch] = React.useState<string>("");
  const debouncedEventSearch = useDebounce(eventSearch, 500);

  const shouldFetchEvents = Boolean(debouncedEventSearch);

  const { data: eventsPage, isLoading: isLoadingEvents } = useGetEventsQuery(
    shouldFetchEvents ? { name: debouncedEventSearch, limit: 50 } : { name: "", limit: 1 },
    { skip: !shouldFetchEvents }
  );
  const events = eventsPage?.events;

  const eventOptions = React.useMemo(() => {
    const defaultOption = { id: 0, name: "General (Sin evento específico)" };
    if (!events) return [defaultOption];
    return [
      defaultOption,
      ...events.map(event => ({
        ...event,
        name: `${event.name} - ${event.id}`,
      }))
    ];
  }, [events]);

  const usersData = React.useMemo(() => (segmentedData as any)?.data?.users || [], [segmentedData]);

  const communicationEligibleUsers = React.useMemo(
    () => usersData.filter((user: User) => !isUserRestrictedByBan(user)),
    [usersData]
  );

  const excludedUsersCount = React.useMemo(() => usersData.length - communicationEligibleUsers.length, [usersData.length, communicationEligibleUsers.length]);

  React.useEffect(() => {
    setSelectedUsersMap((prev) => {
      let changed = false;
      const next = new Map(prev);
      next.forEach((user, id) => {
        if (isUserRestrictedByBan(user)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [usersData]);

  const applyFilters = () => {
    const filters: FilterTypes = {};

    if (email && email.trim() !== "") filters.email = email;
    if (firstName && firstName.trim() !== "") filters.firstName = firstName;
    if (lastName && lastName.trim() !== "") filters.lastName = lastName;
    if (birthDateMonth) filters.birthDateMonth = birthDateMonth;
    if (birthDateDay) filters.birthDateDay = birthDateDay;
    if (eventIds && eventIds.length > 0) filters.eventIds = eventIds;
    if (eventTypeId) filters.eventTypeId = Number(eventTypeId);
    if (eventSubTypeId) filters.eventSubTypeId = Number(eventSubTypeId);
    if (behaviorType) filters.behaviorType = behaviorType;
    if (tenantId) filters.tenantId = tenantId;

    getSegmentedUsers(filters);
  };

  const toggleSelectUser = React.useCallback((user: User) => {
    if (isUserRestrictedByBan(user)) {
      toast.error("No se puede seleccionar un usuario con restricción de acceso");
      return;
    }
    setSelectedUsersMap(prev => {
      const next = new Map(prev);
      if (next.has(user.id)) next.delete(user.id); else next.set(user.id, user);
      return next;
    });
  }, []);

  const allFilteredIds = React.useMemo(
    () => communicationEligibleUsers.map((user: User) => user.id),
    [communicationEligibleUsers]
  );
  const allSelected = React.useMemo(
    () => allFilteredIds.length > 0 && allFilteredIds.every((id: number) => selectedUsersMap.has(id)),
    [allFilteredIds, selectedUsersMap]
  );

  const toggleSelectAllFiltered = React.useCallback(() => {
    setSelectedUsersMap(prev => {
      const next = new Map(prev);
      if (allSelected) {
        allFilteredIds.forEach((id: number) => next.delete(id));
      } else {
        communicationEligibleUsers.forEach((user: User) => next.set(user.id, user));
      }
      return next;
    });
  }, [allSelected, allFilteredIds, communicationEligibleUsers]);

  const validateAndConfirm = () => {
    if (!title || title.trim() === "") {
      toast.error("Falta el título");
      return;
    }
    if (!content || content.trim() === "") {
      toast.error("Falta el contenido");
      return;
    }
    if (selectedUsersMap.size === 0) {
      toast.error("Debe seleccionar al menos un usuario");
      return;
    }
    setShowConfirm(true);
  };

  const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const handleSendCustomMessages = async () => {
    setIsSending(true);
    setSendOutcome(null);
    try {
      const selected = Array.from(selectedUsersMap.values());
      const toEmails = Array.from(
        new Set(
          selected
            .map((u: any) => u.email)
            .filter((e: string): e is string => !!e && e.trim() !== "")
            .map((e: string) => e.trim())
        )
      );

      if (toEmails.length === 0) {
        const msg = "Los usuarios seleccionados no tienen emails válidos";
        setSendOutcome({ errorMessage: msg });
        toast.error(msg);
        return;
      }

      setTotalToSend(toEmails.length);
      setProcessedCount(0);

      const sendChunk = async (emailsChunk: string[]) => {
        const payload = {
          userEmails: emailsChunk,
          title: emojify(title.trim()),
          body: emojify(content.trim()),
          url: url && url.trim() !== "" ? url.trim() : undefined,
          eventId: eventId > 0 ? eventId : undefined,
        };
        const response = await sendCustomMessage(payload).unwrap();

        const responseData = (response as any).data as BulkMessageResponseData;
        return {
          message: responseData?.message || (response as any)?.message,
          stats: responseData?.stats,
          unmatchedEmails: responseData?.unmatchedEmails
        };
      };

      let lastMessage: string | undefined = undefined;
      let aggregatedStats: BulkMessageResponseData['stats'] = {
        totalEmailsProvided: 0,
        usersFound: 0,
        notificationsCreated: 0,
        pushNotificationsSent: 0,
      };
      let aggregatedUnmatched: string[] = [];

      if (toEmails.length <= 100) {
        const { message, stats, unmatchedEmails } = await sendChunk(toEmails as string[]);
        lastMessage = message;
        if (stats) aggregatedStats = stats;
        if (unmatchedEmails) aggregatedUnmatched = unmatchedEmails;
        setProcessedCount(toEmails.length);
      } else {
        const chunkSize = 80;
        for (let i = 0; i < toEmails.length; i += chunkSize) {
          const chunk = toEmails.slice(i, i + chunkSize);
          const { message, stats, unmatchedEmails } = await sendChunk(chunk as string[]);
          lastMessage = message ?? lastMessage;

          if (stats) {
            aggregatedStats.totalEmailsProvided += stats.totalEmailsProvided;
            aggregatedStats.usersFound += stats.usersFound;
            aggregatedStats.notificationsCreated += stats.notificationsCreated;
            aggregatedStats.pushNotificationsSent += stats.pushNotificationsSent;
          }
          if (unmatchedEmails) {
            aggregatedUnmatched = [...aggregatedUnmatched, ...unmatchedEmails];
          }

          setProcessedCount((prev) => Math.min(prev + chunk.length, toEmails.length));
          if (i + chunkSize < toEmails.length) {
            await sleep(3000);
          }
        }
      }

      setSendOutcome({
        stats: aggregatedStats,
        unmatchedEmails: aggregatedUnmatched
      });
      toast.success("Mensaje enviado a los usuarios seleccionados.");
      setSelectedUsersMap(new Map());
    } catch (e: any) {
      const apiError = e;
      console.error("Custom message send error:", apiError);

      let errMsg = "No se pudieron enviar los mensajes. Por favor, reintente más tarde.";

      const getIssues = (obj: any) => {
        if (obj && typeof obj === 'object' && Array.isArray(obj.issues) && obj.issues.length > 0) {
          return obj.issues;
        }
        return null;
      };

      const translateField = (field: string) => {
        const fieldMap: Record<string, string> = {
          'title': 'Título',
          'body': 'Contenido del mensaje',
          'url': 'URL de destino',
          'userEmails': 'Lista de destinatarios'
        };
        return fieldMap[field] || field;
      };

      const translateZodMessage = (message: string) => {
        if (message.includes("valid URL")) return "debe ser una URL válida (ej. https://...)";
        if (message.includes("Required")) return "es obligatorio";
        if (message.includes("String must contain at least")) return "es muy corto";
        return message;
      };

      const data = apiError?.data || apiError;

      const issues = getIssues(data) || getIssues(data?.error) || getIssues(apiError);

      if (issues) {
        const formatted = issues.map((issue: any) => {
          const path = issue.path?.join(".") || "";
          const fieldName = translateField(path);
          const translatedMsg = translateZodMessage(issue.message);
          return `${fieldName}: ${translatedMsg}`;
        }).join(". ");
        errMsg = `Error de validación: ${formatted}`;

      } else if (apiError?.status) {
        if (apiError.status === 400) errMsg = "Solicitud incorrecta. Verifique los datos ingresados.";
        else if (apiError.status === 401) errMsg = "Sesión expirada. Por favor, inicie sesión nuevamente.";
        else if (apiError.status === 403) errMsg = "No tiene permisos para realizar esta acción.";
        else if (apiError.status === 404) errMsg = "No se encontró el recurso solicitado.";
        else if (apiError.status === 500) errMsg = "Error interno del servidor. Contacte a soporte.";
        else errMsg = `Error de conexión (Código ${apiError.status})`;
      }



      if (typeof errMsg !== 'string') {
        try { errMsg = JSON.stringify(errMsg); } catch { errMsg = "Error desconocido al procesar la respuesta"; }
      }

      setSendOutcome({ errorMessage: errMsg });
      toast.error(errMsg);
    } finally {
      setIsSending(false);
    }
  };

  const selectedUsers: User[] = React.useMemo(() => {
    return Array.from(selectedUsersMap.values());
  }, [selectedUsersMap]);

  return (
    <>
      <div className="p-card gap-4 p-3">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-3">
          <h1 className="text-center text-2xl md:text-3xl">Enviar Mensajes Personalizados</h1>
          <Button
            icon="pi pi-history"
            rounded
            outlined
            size="small"
            type="button"
            onClick={() => navigate("/notifications/history")}
            aria-label="Historial Push"
            tooltip="Historial Push"
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
        <div className="grid">
          <div className="col-12 md:col-6">
            <span className="p-float-label w-full">
              <InputText
                id="messageTitle"
                className="w-full"
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
              />
              <label htmlFor="messageTitle">Título del mensaje</label>
            </span>
          </div>
          <div className="col-12 md:col-6 mt-3 md:mt-0">
            <div className="p-inputgroup">
              <span className="p-float-label w-full">
                <Dropdown
                  id="messageEvent"
                  className="w-full"
                  value={eventId}
                  onChange={(e) => setEventId(e.value)}
                  options={eventOptions}
                  optionLabel="name"
                  optionValue="id"
                  filter
                  onFilter={(e) => setEventSearch(e.filter)}
                  loading={isLoadingEvents}
                />
                <label htmlFor="messageEvent">Evento Relacionado (Opcional)</label>
              </span>
              <Button
                icon="pi pi-times"
                className="p-button-danger"
                onClick={() => {
                  setEventId(0);
                  setEventSearch("");
                }}
              />
            </div>
          </div>
          <div className="col-12 mt-3">
            <span className="p-float-label w-full">
              <InputText
                id="messageUrl"
                className="w-full"
                value={url}
                onChange={(e) => setUrl(e.currentTarget.value)}
                placeholder="https://ejemplo.com/evento/123"
              />
              <label htmlFor="messageUrl">URL de destino (opcional)</label>
            </span>
          </div>
          <div className="col-12 mt-3">
            <span className="p-float-label w-full">
              <InputTextarea
                id="messageContent"
                className={`w-full ${content.length > 1000 ? 'p-invalid' : ''}`}
                autoResize
                rows={4}
                value={content}
                onChange={(e) => setContent((e.target as HTMLTextAreaElement).value)}
              />
              <label htmlFor="messageContent">Contenido del mensaje</label>
            </span>
            <small className={`block ${content.length > 1000 ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
              {content.length}/1000 caracteres {content.length > 1000 && `(${content.length - 1000} excedidos)`}
            </small>
          </div>
        </div>
      </div>
      <div className="p-card gap-4 p-3 mt-4">
        <Panel header="Filtros de Segmentación" toggleable collapsed={false}>
          <UserSegmentationFilters
            email={email}
            setEmail={setEmail}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            birthDateMonth={birthDateMonth}
            setBirthDateMonth={setBirthDateMonth}
            birthDateDay={birthDateDay}
            setBirthDateDay={setBirthDateDay}
            eventIds={eventIds}
            setEventIds={setEventIds}
            eventTypeId={eventTypeId}
            setEventTypeId={setEventTypeId}
            eventSubTypeId={eventSubTypeId}
            setEventSubTypeId={setEventSubTypeId}
            behaviorType={behaviorType}
            setBehaviorType={setBehaviorType}
            tenantId={tenantId}
            setTenantId={setTenantId}
            onApplyFilters={applyFilters}
          />
        </Panel>
      </div>
      <div className="p-card gap-4 p-3 mt-4">
        <div className="flex flex-column md:flex-row w-full justify-content-between align-items-center gap-3 mt-2 mb-4">
          <h2 className="text-center m-0 text-xl md:text-2xl">
            Usuarios Segmentados {(segmentedData as any)?.data?.total ? `(${(segmentedData as any).data.total})` : ""}
          </h2>
          <Button
            className="w-full md:w-auto"
            label={`Enviar a ${selectedUsersMap.size} usuario${selectedUsersMap.size !== 1 ? 's' : ''}`}
            icon="pi pi-send"
            onClick={validateAndConfirm}
            disabled={isSending || selectedUsersMap.size === 0}
          />
        </div>

        <div className="w-full overflow-auto">
          <DataTable
            key={`selection-${selectedUsersMap.size}`}
            value={communicationEligibleUsers}
            loading={isLoading}
            responsiveLayout="scroll"
            dataKey="id"
            className="p-datatable-sm"
            rowClassName={(rowData: User) => selectedUsersMap.has(rowData.id) ? "surface-100" : ""}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            emptyMessage="No se encontraron usuarios con los filtros aplicados"
          >
            <Column
              header={
                <div className="flex justify-content-center">
                  <Checkbox onChange={toggleSelectAllFiltered} checked={allSelected} />
                </div>
              }
              body={(rowData: User) => (
                <MemoizedUserCheckbox
                  id={rowData.id}
                  user={rowData}
                  isSelected={selectedUsersMap.has(rowData.id)}
                  onToggle={toggleSelectUser}
                />
              )}
              style={{ width: '3rem' }}
            />
            <Column field="id" header="ID" style={{ width: '5rem' }} />
            <Column field="email" header="Email" style={{ minWidth: '12rem' }} />
            <Column field="lastName" header="Apellido" style={{ minWidth: '10rem' }} />
            <Column field="firstName" header="Nombre" style={{ minWidth: '10rem' }} />
            <Column field="userPhone" header="Teléfono" body={UserPhoneBodyTemplate} style={{ minWidth: '10rem' }} />
            <Column field="createDate" header="Fecha de creación" body={DateBodyTemplate} style={{ minWidth: '10rem' }} />
            <Column
              field="tenants"
              header="Tenant"
              body={(rowData: any) => (rowData.tenants && rowData.tenants.length > 0 ? rowData.tenants.map((t: any) => t.provider).join(", ") : "-")}
              style={{ minWidth: '8rem' }}
            />
            <Column
              field="banned"
              header="Estado"
              alignHeader="center"
              style={{ width: '5rem', textAlign: 'center' }}
              body={(rowData: User) => (
                <i
                  className={`pi ${isUserRestrictedByBan(rowData) ? 'pi-ban text-red-500' : 'pi-check text-green-500'}`}
                  style={{ fontSize: '1.2rem' }}
                  title={isUserRestrictedByBan(rowData) ? 'Usuario restringido' : 'Usuario activo'}
                />
              )}
            />
          </DataTable>
          {excludedUsersCount > 0 && (
            <div className="mt-3 text-orange-600 font-medium">
              Se excluyeron automáticamente {excludedUsersCount} usuario(s) con restricción de acceso.
            </div>
          )}
        </div>
      </div>
      <Dialog
        header="PRECAUCIÓN: ¿Enviar mensajes personalizados?"
        visible={showConfirm}
        style={{ width: "90vw", maxWidth: "600px" }}
        onHide={() => { setShowConfirm(false); setSendOutcome(null); }}
        footer={
          sendOutcome ? null : (
            <div>
              <Button
                severity="success"
                label={isSending ? "Enviando..." : "Enviar"}
                icon={isSending ? "pi pi-spin pi-spinner" : "pi pi-send"}
                disabled={isSending}
                onClick={handleSendCustomMessages}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={() => setShowConfirm(false)}
                disabled={isSending}
              />
            </div>
          )
        }
      >
        <div className="mb-3">
          <h1 className="text-2xl md:text-3xl text-red-500 m-0 p-0">
            <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
          </h1>
        </div>
        <div className="mb-2 text-lg"><b>Título:</b> {emojify(title)}</div>
        <div className="mb-2"><b>Contenido:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{emojify(content)}</span></div>
        {url && url.trim() !== "" && (
          <div className="mb-2 overflow-hidden text-overflow-ellipsis"><b>URL:</b> {url}</div>
        )}
        {eventId > 0 && (
          <div className="mb-2"><b>Evento:</b> {events?.find(e => e.id === eventId)?.name || `ID: ${eventId}`}</div>
        )}
        <div className="mb-2">
          <b>Usuarios seleccionados ({selectedUsers.length}):</b>
          <div className="surface-100 border-round p-2 mt-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
            <ul className="m-0 pl-3">
              {selectedUsers.map(u => (
                <li key={u.id}>{u.firstName} {u.lastName} ({u.email})</li>
              ))}
            </ul>
          </div>
        </div>
        {sendOutcome && (
          <div className="mt-3">
            <h3 className="mt-0">Resultado del envío</h3>
            {totalToSend > 0 && (
              <div className="mb-3">
                <div className="mb-1">Progreso: {processedCount} / {totalToSend}</div>
                <ProgressBar value={Math.round((processedCount / totalToSend) * 100)}></ProgressBar>
              </div>
            )}
            {sendOutcome.errorMessage ? (
              <div className="text-red-600 font-bold">{sendOutcome.errorMessage}</div>
            ) : (
              <div className="mt-3">
                <div className="text-green-600 font-bold mb-3">Mensajes enviados exitosamente.</div>

                {sendOutcome.unmatchedEmails && sendOutcome.unmatchedEmails.length > 0 && (
                  <div className="mt-3">
                    <div className="text-red-500 font-semibold mb-1">Emails no encontrados ({sendOutcome.unmatchedEmails.length}):</div>
                    <div className="surface-100 border-round p-2" style={{ maxHeight: "100px", overflowY: "auto" }}>
                      <ul className="m-0 pl-3 text-sm">
                        {sendOutcome.unmatchedEmails.map((email, i) => (
                          <li key={i}>{email}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
};
